/**
 * Pre-expiry warning: "your session is about to idle out" (brief §2.8 keeps
 * the timeout; this fixes the *silence*, not the policy).
 *
 * The server restarts the idle clock on every authenticated request, so the
 * client mirrors it: last successful request + `idle_minutes` is the
 * deadline. Inside the final five minutes a banner offers "Stay signed in"
 * (one lightweight `/me` ping restarts the clock). If the deadline passes,
 * one probe fires so the 401 routes into the session-expired flow at once —
 * the user sees the login notice, not a frozen page.
 */

import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { getCurrentSession, getLastActivityAt } from "@pbxmgr/api";
import { Button } from "@pbxmgr/ui";

import { useAuth } from "../auth/AuthProvider";

const WARN_BEFORE_MS = 5 * 60_000;
const TICK_MS = 30_000;

export function SessionExpiryBanner(): ReactNode {
  const { session } = useAuth();
  const [remainingMs, setRemainingMs] = useState<number | null>(null);
  const [probed, setProbed] = useState(false);

  useEffect(() => {
    if (!session) {
      setRemainingMs(null);
      return;
    }
    const tick = (): void => {
      const deadline = getLastActivityAt() + session.idle_minutes * 60_000;
      const remaining = deadline - Date.now();
      setRemainingMs(remaining <= WARN_BEFORE_MS ? remaining : null);
      if (remaining <= 0 && !probed) {
        // Past the deadline: let the 401 surface the expired-session flow
        // immediately instead of waiting for the user's next click.
        setProbed(true);
        void getCurrentSession();
      }
      if (remaining > 0 && probed) setProbed(false);
    };
    tick();
    const timer = setInterval(tick, TICK_MS);
    return () => clearInterval(timer);
  }, [session, probed]);

  if (!session || remainingMs === null || remainingMs <= 0) return null;

  const minutes = Math.max(1, Math.ceil(remainingMs / 60_000));
  return (
    <div className="session-expiry-banner" role="status">
      <span>
        Your session expires in about {minutes} minute{minutes === 1 ? "" : "s"} unless you
        stay active.
      </span>
      <Button
        variant="secondary"
        onClick={() => {
          // A successful /me restarts the idle clock server- and client-side.
          void getCurrentSession().then(() => setRemainingMs(null));
        }}
      >
        Stay signed in
      </Button>
    </div>
  );
}
